import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { CheckCircle2, AlertCircle, Loader2, Plane, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_POLLS = 15;

export default function AssessmentConfirmation() {
  const [phase, setPhase] = useState("checking"); // checking | ready | error
  const [info, setInfo] = useState(null); // { firstName, reportUrl }
  const [errorMsg, setErrorMsg] = useState("");
  const polls = useRef(0);

  useEffect(() => {
    const sessionId = new URLSearchParams(window.location.search).get("session_id");

    const fail = (msg) => {
      setErrorMsg(msg);
      setPhase("error");
    };

    const pollStatus = async () => {
      if (polls.current >= MAX_POLLS) {
        fail("We couldn't confirm your payment in time. If you were charged, your assessment is on its way by email.");
        return;
      }
      polls.current += 1;
      try {
        const { data } = await axios.get(`${API}/assessment/checkout/status/${sessionId}`);
        if (data.payment_status === "paid" && data.report_url) {
          setInfo({ firstName: data.first_name, reportUrl: data.report_url });
          setPhase("ready");
        } else if (data.status === "expired") {
          fail("Your checkout session expired before payment was completed. No charge was made — head back and try again.");
        } else {
          setTimeout(pollStatus, 2000);
        }
      } catch {
        fail("Something went wrong while confirming your payment.");
      }
    };

    if (sessionId) pollStatus();
    else fail("This page needs a valid checkout session.");
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only, reads session_id from URL
  }, []);

  return (
    <div className="min-h-screen bg-[var(--cream)] flex flex-col" data-testid="assessment-confirmation-page">
      <header className="px-5 md:px-8 py-5">
        <a href="/" className="inline-flex items-center gap-2 font-display font-extrabold text-[var(--ink)]">
          <Plane size={18} /> The Ditch Collective
        </a>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 pb-20">
        <div className="w-full max-w-lg bg-[var(--paper)] rounded-3xl border border-[var(--line)] p-8 md:p-10 text-center shadow-xl">
          {phase === "checking" && (
            <>
              <Loader2 size={40} className="mx-auto animate-spin text-[var(--ink)]" />
              <h1 className="font-display text-2xl font-extrabold mt-6">Building your assessment…</h1>
              <p className="text-[var(--ocean)] mt-2 text-sm">Confirming payment and generating your PDF — a few seconds.</p>
            </>
          )}

          {phase === "ready" && (
            <>
              <CheckCircle2 size={44} className="mx-auto text-[var(--lime-deep)]" />
              <span className="num-tag text-xs block mt-6">Relocation assessment</span>
              <h1 className="font-display text-3xl font-extrabold mt-2 leading-tight">
                It's ready{info.firstName ? `, ${info.firstName}` : ""}.
              </h1>
              <p className="text-[var(--ocean)] mt-3 text-sm leading-relaxed">
                Your personalised assessment is below — salary benchmark, suburb shortlist, and the package
                we'd recommend for your move. You're also on the member newsletter now.
              </p>
              <a
                data-testid="assessment-report-download"
                href={info.reportUrl}
                className="mt-7 inline-flex items-center justify-center gap-2 bg-[var(--ink)] text-[var(--paper)] font-display font-bold rounded-xl px-8 py-3.5 hover:bg-[var(--ink-soft)] transition-colors"
              >
                <Download size={18} /> Download your assessment (PDF)
              </a>
              <p className="text-xs text-[var(--ocean)] mt-5">
                Link valid for 30 days — we've also sent it by email.
              </p>
            </>
          )}

          {phase === "error" && (
            <>
              <AlertCircle size={44} className="mx-auto text-[var(--ink)]" />
              <h1 className="font-display text-2xl font-extrabold mt-6" data-testid="assessment-confirmation-error">
                Hmm, that didn't work
              </h1>
              <p className="text-[var(--ocean)] mt-3 text-sm leading-relaxed">{errorMsg}</p>
              <p className="text-sm text-[var(--ink)] mt-5">
                Flick us an email at{" "}
                <a href="mailto:hello@theditchcollective.co.nz" className="font-bold underline">
                  hello@theditchcollective.co.nz
                </a>{" "}
                and we'll sort you out.
              </p>
              <a href="/assessment" className="mt-6 inline-block btn-ghost text-sm">← Try again</a>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
